import React, { useMemo } from 'react';
import { Box, Grid } from '@mui/material';
import {
  Contacts as ContactsIcon,
  LocalOffer as TagIcon,
  TouchApp as InteractionIcon,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { LeadsMetrics, CHART_COLORS } from '../../types/dashboard.types';
import ChartCard from './ChartCard';
import SectionHeader from './SectionHeader';
import EmptyState from './EmptyState';
import TopExhibitorsTable from './TopExhibitorsTable';

export interface LeadsMetricsSectionProps {
  data: LeadsMetrics;
  loading?: boolean;
}

const LeadsMetricsSection: React.FC<LeadsMetricsSectionProps> = ({
  data,
  loading = false,
}) => {
  const tagData = useMemo(
    () =>
      data.leadsByTag.map((item, index) => ({
        name: item.tag,
        value: item.count,
        color: CHART_COLORS.palette[index % CHART_COLORS.palette.length],
      })),
    [data.leadsByTag]
  );

  const interactionTypeData = useMemo(
    () =>
      data.interactionsByType.map((item, index) => ({
        name: item.type,
        count: item.count,
        fill: CHART_COLORS.palette[index % CHART_COLORS.palette.length],
      })),
    [data.interactionsByType]
  );

  const outcomeData = useMemo(
    () =>
      data.interactionsByOutcome.map((item, index) => ({
        name: item.outcome,
        value: item.count,
        color: CHART_COLORS.palette[index % CHART_COLORS.palette.length],
      })),
    [data.interactionsByOutcome]
  );

  const topExhibitorsData = useMemo(
    () =>
      data.topExhibitorsByLeads.map(item => ({
        exhibitorId: item.exhibitorId,
        exhibitorName: item.exhibitorName,
        count: item.leadCount,
      })),
    [data.topExhibitorsByLeads]
  );

  const hasTagData = tagData.length > 0;
  const hasInteractionData = interactionTypeData.length > 0;
  const hasOutcomeData = outcomeData.length > 0;

  return (
    <Box>
      <SectionHeader title="Leads & Interactions" icon={<ContactsIcon />} />

      <Grid container spacing={3}>
        {/* Charts Row 1 */}
        <Grid item xs={12} md={6}>
          <ChartCard
            title="Leads by Tag"
            subtitle="Distribution by lead tags"
            height={280}
            loading={loading}
          >
            {!hasTagData ? (
              <EmptyState
                message="No lead tags available"
                icon={<TagIcon sx={{ fontSize: 32, color: '#9ca3af' }} />}
                height={250}
              />
            ) : (
              <>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={tagData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {tagData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    gap: 2,
                  }}
                >
                  {tagData.slice(0, 5).map((item, index) => (
                    <Box
                      key={index}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: item.color,
                        }}
                      />
                      <Box sx={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        {item.name}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartCard
            title="Interactions by Type"
            subtitle="Types of lead interactions"
            height={280}
            loading={loading}
          >
            {!hasInteractionData ? (
              <EmptyState
                message="No interaction data available"
                icon={<InteractionIcon sx={{ fontSize: 32, color: '#9ca3af' }} />}
                height={250}
              />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={interactionTypeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" stroke="#6b7280" tick={{ fontSize: 11 }} />
                  <YAxis stroke="#6b7280" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    }}
                  />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>

        {/* Charts Row 2 */}
        <Grid item xs={12} md={6}>
          <ChartCard
            title="Interactions by Outcome"
            subtitle="Results of interactions"
            height={280}
            loading={loading}
          >
            {!hasOutcomeData ? (
              <EmptyState message="No outcome data available" height={250} />
            ) : (
              <>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={outcomeData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) =>
                        `${name}: ${(percent * 100).toFixed(0)}%`
                      }
                    >
                      {outcomeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </>
            )}
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <TopExhibitorsTable
            title="Top Exhibitors by Leads"
            data={topExhibitorsData}
            countLabel="Leads"
            emptyMessage="No lead data for exhibitors"
            maxItems={5}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default LeadsMetricsSection;
